import React, { useEffect, useRef, useState } from "react";
import { getSttProvider, setSttProvider, getAiModel, setAiModel, getDailyUsage, getUsageLimits, setUsageLimits, practiceAdminSearchUsers, practiceAdminResetUser, practiceAdminMisgenderingReports, practiceAdminSetGender, practiceAdminSetDob, importQuranCurriculum, adminListVouchers, adminCreateVoucher, adminRevokeVoucher, adminListInstitutes, adminApproveInstitute, adminDisableInstitute, adminDeleteInstitute } from "../api";
import AdminQuizEditor from "./AdminQuizEditor";
import AdminCostsPanel from "./AdminCostsPanel";
import AdminCategoryGroupsPanel from "./AdminCategoryGroupsPanel";
import { todayIso } from "../utils/age";

export default function AdminSettingsPanel({ onClose }) {
  const [activeTab, setActiveTab] = useState("settings"); // "settings" | "quiz" | "practice"
  const [sttProvider, setSttProviderState] = useState(null);
  const [aiModel, setAiModelState] = useState(null);
  const [limits, setLimits] = useState({ max_daily_roleplay: 3, free_chat_minutes: 10, max_response_seconds: 30, require_topic_completion_for_calls: true, min_practice_overlap_minutes: 15 });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saveMsg, setSaveMsg] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    Promise.all([getSttProvider(), getAiModel(), getUsageLimits()])
      .then(([stt, ai, limits]) => {
        setSttProviderState(stt.provider);
        setAiModelState(ai.model);
        setLimits(limits || { max_daily_roleplay: 3, free_chat_minutes: 10, max_response_seconds: 30, require_topic_completion_for_calls: true, min_practice_overlap_minutes: 15 });
      })
      .catch(() => setError("Failed to load settings"))
      .finally(() => setLoading(false));
  }, []);

  const handleSave = async () => {
    setSaving(true);
    setSaveMsg(null);
    setError(null);
    try {
      await Promise.all([
        setSttProvider(sttProvider),
        setAiModel(aiModel),
        setUsageLimits(limits),
      ]);
      setSaveMsg("Settings saved!");
    } catch (e) {
      setError(e.message || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const TABS = [
    { id: "settings", label: "App Settings" },
    { id: "institutes", label: "Institutes" },
    { id: "groups", label: "Category groups" },
    { id: "vouchers", label: "Vouchers" },
    { id: "costs", label: "Costs" },
    { id: "quiz", label: "Quiz Editor" },
    { id: "practice", label: "Practice Calls" },
    { id: "quran", label: "Quran Import" },
  ];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-[3px] p-4" onClick={onClose}>
      <div
        className="surface-glass dark-scope rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100 shrink-0">
          <div className="flex items-center gap-2">
            <svg className="w-5 h-5 text-dt-gold-bright" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
              <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
            </svg>
            <h2 className="text-lg font-semibold text-slate-900">Admin Panel</h2>
            <span className="ml-1 text-xs font-medium px-1.5 py-0.5 rounded bg-[var(--gold-bg)] text-dt-gold-bright border border-[var(--panel-line)]">Admin</span>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700 p-1 rounded-full hover:bg-slate-100 transition-colors">
            <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-slate-100 px-6 shrink-0 overflow-x-auto">
          {TABS.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`px-4 py-2.5 text-sm font-medium border-b-2 transition-colors -mb-px whitespace-nowrap shrink-0 ${
                activeTab === tab.id
                  ? "border-[var(--gold)] text-dt-gold-bright"
                  : "border-transparent text-slate-500 hover:text-slate-700"
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-6 py-5">
          {activeTab === "settings" && (
            <div className="space-y-6">
              {loading && <p className="text-sm text-slate-500">Loading settings…</p>}
              {error && <p className="text-sm text-red-600">{error}</p>}

              {!loading && (
                <>
                  {/* STT Provider */}
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1.5">Speech-to-Text Provider</label>
                    <select
                      value={sttProvider || ""}
                      onChange={(e) => setSttProviderState(e.target.value)}
                      className="w-full px-3 py-2 rounded-lg border border-slate-200 bg-white text-base focus:outline-none focus:ring-2 focus:ring-[var(--gold)]"
                    >
                      <option value="google">Google Cloud STT</option>
                      <option value="openai">OpenAI Whisper</option>
                    </select>
                  </div>

                  {/* AI Model */}
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1.5">AI Conversation Model</label>
                    <select
                      value={aiModel || ""}
                      onChange={(e) => setAiModelState(e.target.value)}
                      className="w-full px-3 py-2 rounded-lg border border-slate-200 bg-white text-base focus:outline-none focus:ring-2 focus:ring-[var(--gold)]"
                    >
                      <option value="gpt-5">GPT-5</option>
                      <option value="gpt-5-mini">GPT-5 Mini</option>
                      <option value="gpt-5-nano">GPT-5 Nano</option>
                      <option value="gpt-4.1">GPT-4.1</option>
                      <option value="gpt-4.1-mini">GPT-4.1 Mini</option>
                      <option value="gpt-4o">GPT-4o</option>
                      <option value="gpt-4o-mini">GPT-4o Mini</option>
                      <option value="gpt-4-turbo">GPT-4 Turbo</option>
                      <option value="gpt-4">GPT-4</option>
                      <option value="gpt-3.5-turbo">GPT-3.5 Turbo</option>
                    </select>
                  </div>

                  {/* Usage Limits */}
                  <div>
                    <h3 className="text-sm font-medium text-slate-700 mb-3">Usage Limits</h3>
                    <div className="space-y-3">
                      <div className="flex items-center justify-between">
                        <label className="text-sm text-slate-600">Max roleplay sessions / day</label>
                        <input
                          type="number" min="1" max="20" inputMode="numeric"
                          value={limits.max_daily_roleplay}
                          onChange={(e) => setLimits((l) => ({ ...l, max_daily_roleplay: parseInt(e.target.value) || 1 }))}
                          className="w-20 px-2 py-1.5 rounded-lg border border-slate-200 text-base text-center focus:outline-none focus:ring-2 focus:ring-[var(--gold)]"
                        />
                      </div>
                      <div className="flex items-center justify-between">
                        <label className="text-sm text-slate-600">Free chat minutes / day</label>
                        <input
                          type="number" min="1" max="120" inputMode="numeric"
                          value={limits.free_chat_minutes}
                          onChange={(e) => setLimits((l) => ({ ...l, free_chat_minutes: parseInt(e.target.value) || 1 }))}
                          className="w-20 px-2 py-1.5 rounded-lg border border-slate-200 text-base text-center focus:outline-none focus:ring-2 focus:ring-[var(--gold)]"
                        />
                      </div>
                      <div className="flex items-center justify-between">
                        <label className="text-sm text-slate-600">Max recording seconds</label>
                        <input
                          type="number" min="5" max="120" inputMode="numeric"
                          value={limits.max_response_seconds}
                          onChange={(e) => setLimits((l) => ({ ...l, max_response_seconds: parseInt(e.target.value) || 5 }))}
                          className="w-20 px-2 py-1.5 rounded-lg border border-slate-200 text-base text-center focus:outline-none focus:ring-2 focus:ring-[var(--gold)]"
                        />
                      </div>
                      <div className="flex items-center justify-between">
                        <label className="text-sm text-slate-600">OTP emails per IP / hour (0 = off)</label>
                        <input
                          type="number" min="0" max="1000" inputMode="numeric"
                          value={limits.otp_ip_hourly_limit ?? 10}
                          onChange={(e) => setLimits((l) => ({ ...l, otp_ip_hourly_limit: Math.max(0, parseInt(e.target.value) || 0) }))}
                          className="w-20 px-2 py-1.5 rounded-lg border border-slate-200 text-base text-center focus:outline-none focus:ring-2 focus:ring-[var(--gold)]"
                        />
                      </div>
                      <div className="flex items-center justify-between">
                        <label className="text-sm text-slate-600">OTP emails per address / hour (0 = off)</label>
                        <input
                          type="number" min="0" max="1000" inputMode="numeric"
                          value={limits.otp_email_hourly_limit ?? 3}
                          onChange={(e) => setLimits((l) => ({ ...l, otp_email_hourly_limit: Math.max(0, parseInt(e.target.value) || 0) }))}
                          className="w-20 px-2 py-1.5 rounded-lg border border-slate-200 text-base text-center focus:outline-none focus:ring-2 focus:ring-[var(--gold)]"
                        />
                      </div>
                      <div className="flex items-center justify-between gap-3">
                        <label className="text-sm text-slate-600 shrink-0">Support email</label>
                        <input
                          type="email"
                          placeholder="blank = EMAIL_REPLY_TO"
                          value={limits.support_email ?? ""}
                          onChange={(e) => setLimits((l) => ({ ...l, support_email: e.target.value.trim() }))}
                          className="w-56 px-2 py-1.5 rounded-lg border border-slate-200 text-base focus:outline-none focus:ring-2 focus:ring-[var(--gold)]"
                        />
                      </div>
                      <div className="flex items-center justify-between">
                        <label className="text-sm text-slate-600">Word Bank words / day</label>
                        <input
                          type="number" min="1" max="200" inputMode="numeric"
                          value={limits.max_daily_word_bank_additions ?? 10}
                          onChange={(e) => setLimits((l) => ({ ...l, max_daily_word_bank_additions: parseInt(e.target.value) || 1 }))}
                          className="w-20 px-2 py-1.5 rounded-lg border border-slate-200 text-base text-center focus:outline-none focus:ring-2 focus:ring-[var(--gold)]"
                        />
                      </div>
                      <div className="flex items-center justify-between">
                        <label className="text-sm text-slate-600">Sentence AI asks / day</label>
                        <input
                          type="number" min="1" max="200" inputMode="numeric"
                          value={limits.max_daily_sentence_ai_asks ?? 10}
                          onChange={(e) => setLimits((l) => ({ ...l, max_daily_sentence_ai_asks: parseInt(e.target.value) || 1 }))}
                          className="w-20 px-2 py-1.5 rounded-lg border border-slate-200 text-base text-center focus:outline-none focus:ring-2 focus:ring-[var(--gold)]"
                        />
                      </div>
                      <div className="flex items-center justify-between">
                        <label className="text-sm text-slate-600">Speaking Helper asks / day</label>
                        <input
                          type="number" min="1" max="200" inputMode="numeric"
                          value={limits.max_daily_speaking_help ?? 20}
                          onChange={(e) => setLimits((l) => ({ ...l, max_daily_speaking_help: parseInt(e.target.value) || 1 }))}
                          className="w-20 px-2 py-1.5 rounded-lg border border-slate-200 text-base text-center focus:outline-none focus:ring-2 focus:ring-[var(--gold)]"
                        />
                      </div>
                      <div className="flex items-center justify-between">
                        <label className="text-sm text-slate-600">Max saved sentences</label>
                        <input
                          type="number" min="1" max="500" inputMode="numeric"
                          value={limits.max_saved_sentences ?? 50}
                          onChange={(e) => setLimits((l) => ({ ...l, max_saved_sentences: parseInt(e.target.value) || 1 }))}
                          className="w-20 px-2 py-1.5 rounded-lg border border-slate-200 text-base text-center focus:outline-none focus:ring-2 focus:ring-[var(--gold)]"
                        />
                      </div>
                      <div className="flex items-center justify-between">
                        <label className="text-sm text-slate-600">Word AI lookups / day</label>
                        <input
                          type="number" min="1" max="500" inputMode="numeric"
                          value={limits.max_daily_word_analyze_asks ?? 20}
                          onChange={(e) => setLimits((l) => ({ ...l, max_daily_word_analyze_asks: parseInt(e.target.value) || 1 }))}
                          className="w-20 px-2 py-1.5 rounded-lg border border-slate-200 text-base text-center focus:outline-none focus:ring-2 focus:ring-[var(--gold)]"
                        />
                      </div>
                      <div className="flex items-center justify-between">
                        <label className="text-sm text-slate-600">OCR pages / day (Reading)</label>
                        <input
                          type="number" min="1" max="1000" inputMode="numeric"
                          value={limits.max_daily_ocr_pages ?? 50}
                          onChange={(e) => setLimits((l) => ({ ...l, max_daily_ocr_pages: parseInt(e.target.value) || 1 }))}
                          className="w-20 px-2 py-1.5 rounded-lg border border-slate-200 text-base text-center focus:outline-none focus:ring-2 focus:ring-[var(--gold)]"
                        />
                      </div>
                      <div className="flex items-center justify-between">
                        <label className="text-sm text-slate-600">
                          Sentence translations / day (Reading)
                          <span className="block text-xs text-slate-400">Cache hits are free and don't count.</span>
                        </label>
                        <input
                          type="number" min="1" max="500" inputMode="numeric"
                          value={limits.max_daily_sentence_translations ?? 20}
                          onChange={(e) => setLimits((l) => ({ ...l, max_daily_sentence_translations: parseInt(e.target.value) || 1 }))}
                          className="w-20 px-2 py-1.5 rounded-lg border border-slate-200 text-base text-center focus:outline-none focus:ring-2 focus:ring-[var(--gold)]"
                        />
                      </div>
                      <div className="flex items-center justify-between">
                        <label className="text-sm text-slate-600">Translation max sentence length (chars)</label>
                        <input
                          type="number" min="20" max="2000" inputMode="numeric"
                          value={limits.translation_max_chars ?? 300}
                          onChange={(e) => setLimits((l) => ({ ...l, translation_max_chars: parseInt(e.target.value) || 20 }))}
                          className="w-20 px-2 py-1.5 rounded-lg border border-slate-200 text-base text-center focus:outline-none focus:ring-2 focus:ring-[var(--gold)]"
                        />
                      </div>
                      <div className="flex items-center justify-between">
                        <label className="text-sm text-slate-600">AI book-topic enrich / day</label>
                        <input
                          type="number" min="1" max="100" inputMode="numeric"
                          value={limits.max_daily_book_topic_ai ?? 5}
                          onChange={(e) => setLimits((l) => ({ ...l, max_daily_book_topic_ai: parseInt(e.target.value) || 1 }))}
                          className="w-20 px-2 py-1.5 rounded-lg border border-slate-200 text-base text-center focus:outline-none focus:ring-2 focus:ring-[var(--gold)]"
                        />
                      </div>
                      <div className="flex items-center justify-between">
                        <label className="text-sm text-slate-600">Max words / book topic</label>
                        <input
                          type="number" min="5" max="200" inputMode="numeric"
                          value={limits.max_book_topic_words ?? 40}
                          onChange={(e) => setLimits((l) => ({ ...l, max_book_topic_words: parseInt(e.target.value) || 5 }))}
                          className="w-20 px-2 py-1.5 rounded-lg border border-slate-200 text-base text-center focus:outline-none focus:ring-2 focus:ring-[var(--gold)]"
                        />
                      </div>
                      <div className="flex items-center justify-between">
                        <label className="text-sm text-slate-600">Max topics / user</label>
                        <input
                          type="number" min="1" max="500" inputMode="numeric"
                          value={limits.max_user_topics ?? 5}
                          onChange={(e) => setLimits((l) => ({ ...l, max_user_topics: parseInt(e.target.value) || 1 }))}
                          className="w-20 px-2 py-1.5 rounded-lg border border-slate-200 text-base text-center focus:outline-none focus:ring-2 focus:ring-[var(--gold)]"
                        />
                      </div>

                      {/* ── Social feed (gender-segregated, fuṣḥā-only) ── */}
                      <p className="text-xs font-semibold uppercase tracking-wide text-slate-400 pt-2">Social Feed</p>
                      <div className="flex items-center justify-between">
                        <label className="text-sm text-slate-600">Feed enabled (launch switch)</label>
                        <input
                          type="checkbox"
                          checked={limits.feed_enabled ?? false}
                          onChange={(e) => setLimits((l) => ({ ...l, feed_enabled: e.target.checked }))}
                        />
                      </div>
                      <div className="flex items-center justify-between">
                        <label className="text-sm text-slate-600">Hide feed button (non-admins)</label>
                        <input
                          type="checkbox"
                          checked={limits.feed_button_hidden ?? false}
                          onChange={(e) => setLimits((l) => ({ ...l, feed_button_hidden: e.target.checked }))}
                        />
                      </div>
                      <div className="flex items-center justify-between">
                        <label className="text-sm text-slate-600">Feed requires 18+</label>
                        <input
                          type="checkbox"
                          checked={limits.feed_requires_18 ?? true}
                          onChange={(e) => setLimits((l) => ({ ...l, feed_requires_18: e.target.checked }))}
                        />
                      </div>
                      <div className="flex items-center justify-between">
                        <label className="text-sm text-slate-600">Feed posts / day</label>
                        <input
                          type="number" min="1" max="100" inputMode="numeric"
                          value={limits.max_daily_feed_posts ?? 5}
                          onChange={(e) => setLimits((l) => ({ ...l, max_daily_feed_posts: parseInt(e.target.value) || 1 }))}
                          className="w-20 px-2 py-1.5 rounded-lg border border-slate-200 text-base text-center focus:outline-none focus:ring-2 focus:ring-[var(--gold)]"
                        />
                      </div>
                      <div className="flex items-center justify-between">
                        <label className="text-sm text-slate-600">Feed comments / day</label>
                        <input
                          type="number" min="1" max="200" inputMode="numeric"
                          value={limits.max_daily_feed_comments ?? 20}
                          onChange={(e) => setLimits((l) => ({ ...l, max_daily_feed_comments: parseInt(e.target.value) || 1 }))}
                          className="w-20 px-2 py-1.5 rounded-lg border border-slate-200 text-base text-center focus:outline-none focus:ring-2 focus:ring-[var(--gold)]"
                        />
                      </div>
                      <div className="flex items-center justify-between">
                        <label className="text-sm text-slate-600">Fuṣḥā AI checks / day</label>
                        <input
                          type="number" min="1" max="500" inputMode="numeric"
                          value={limits.max_daily_fusha_checks ?? 30}
                          onChange={(e) => setLimits((l) => ({ ...l, max_daily_fusha_checks: parseInt(e.target.value) || 1 }))}
                          className="w-20 px-2 py-1.5 rounded-lg border border-slate-200 text-base text-center focus:outline-none focus:ring-2 focus:ring-[var(--gold)]"
                        />
                      </div>
                      <div className="flex items-center justify-between">
                        <label className="text-sm text-slate-600">Borderline pass score (0–100)</label>
                        <input
                          type="number" min="1" max="100" inputMode="numeric"
                          value={limits.feed_fusha_min_score ?? 70}
                          onChange={(e) => setLimits((l) => ({ ...l, feed_fusha_min_score: parseInt(e.target.value) || 1 }))}
                          className="w-20 px-2 py-1.5 rounded-lg border border-slate-200 text-base text-center focus:outline-none focus:ring-2 focus:ring-[var(--gold)]"
                        />
                      </div>
                      <div className="flex items-center justify-between">
                        <label className="text-sm text-slate-600">Auto-hide after N reports (0 = off)</label>
                        <input
                          type="number" min="0" max="50" inputMode="numeric"
                          value={limits.feed_auto_hide_reports ?? 3}
                          onChange={(e) => setLimits((l) => ({ ...l, feed_auto_hide_reports: parseInt(e.target.value) || 0 }))}
                          className="w-20 px-2 py-1.5 rounded-lg border border-slate-200 text-base text-center focus:outline-none focus:ring-2 focus:ring-[var(--gold)]"
                        />
                      </div>
                      <div className="flex items-center justify-between">
                        <label className="text-sm text-slate-600">Posting unlocks after lesson</label>
                        <select
                          value={limits.feed_posting_min_lesson ?? "harakat_tanwin"}
                          onChange={(e) => setLimits((l) => ({ ...l, feed_posting_min_lesson: e.target.value }))}
                          className="px-2 py-1.5 rounded-lg border border-slate-200 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-[var(--gold)]"
                        >
                          {["home_alef_lam", "home_ta_nun_mim", "home_shin_sin_ya_ba", "home_kaf_ta_marbuta",
                            "top_dad_sad_tha_qaf_fa", "top_ghayn_ayn_ha_kha", "bottom_hamza_ra", "bottom_waw_zay_zah",
                            "hamza_family", "harakat_short_vowels", "harakat_sukun", "harakat_shadda",
                            "harakat_tanwin", "vowelled_words", "sentences", "free_practice"].map((code) => (
                            <option key={code} value={code}>{code}</option>
                          ))}
                        </select>
                      </div>
                      <div className="flex items-center justify-between">
                        <label className="text-sm text-slate-600">Fuṣḥā check model</label>
                        <select
                          value={limits.feed_fusha_model ?? ""}
                          onChange={(e) => setLimits((l) => ({ ...l, feed_fusha_model: e.target.value }))}
                          className="px-2 py-1.5 rounded-lg border border-slate-200 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-[var(--gold)]"
                        >
                          <option value="">Same as AI model</option>
                          <option value="gpt-5-mini">GPT-5 Mini</option>
                          <option value="gpt-5-nano">GPT-5 Nano</option>
                          <option value="gpt-4o-mini">GPT-4o Mini</option>
                          <option value="gpt-4.1-mini">GPT-4.1 Mini</option>
                        </select>
                      </div>
                      {/* ── Iʿrāb tool (analyze any Arabic text) ── */}
                      <p className="text-xs font-semibold uppercase tracking-wide text-slate-400 pt-2">Iʿrāb Tool</p>
                      <div className="flex items-center justify-between">
                        <label className="text-sm text-slate-600">Iʿrāb tool enabled (launch switch)</label>
                        <input
                          type="checkbox"
                          checked={limits.morphology_tool_enabled ?? false}
                          onChange={(e) => setLimits((l) => ({ ...l, morphology_tool_enabled: e.target.checked }))}
                        />
                      </div>
                      <div className="flex items-center justify-between">
                        <label className="text-sm text-slate-600">Structure engine</label>
                        <select
                          value={limits.structure_engine ?? "hybrid"}
                          onChange={(e) => setLimits((l) => ({ ...l, structure_engine: e.target.value }))}
                          className="px-2 py-1.5 rounded-lg border border-slate-200 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-[var(--gold)]"
                        >
                          <option value="hybrid">Hybrid — local + AI refine</option>
                          <option value="openai">OpenAI only</option>
                          <option value="local">Local only (no AI cost)</option>
                        </select>
                      </div>
                      <div className="flex items-center justify-between">
                        <label className="text-sm text-slate-600">Iʿrāb AI model</label>
                        <select
                          value={limits.morphology_ai_model ?? ""}
                          onChange={(e) => setLimits((l) => ({ ...l, morphology_ai_model: e.target.value }))}
                          className="px-2 py-1.5 rounded-lg border border-slate-200 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-[var(--gold)]"
                        >
                          <option value="">Same as AI model</option>
                          <option value="gpt-5">GPT-5</option>
                          <option value="gpt-5-mini">GPT-5 Mini</option>
                          <option value="gpt-5-nano">GPT-5 Nano</option>
                          <option value="gpt-4.1">GPT-4.1</option>
                          <option value="gpt-4.1-mini">GPT-4.1 Mini</option>
                          <option value="gpt-4o">GPT-4o</option>
                          <option value="gpt-4o-mini">GPT-4o Mini</option>
                          <option value="gpt-4-turbo">GPT-4 Turbo</option>
                          <option value="gpt-4">GPT-4</option>
                          <option value="gpt-3.5-turbo">GPT-3.5 Turbo</option>
                        </select>
                      </div>
                      <p className="text-xs text-slate-400 -mt-1">
                        Used for AI refinement in the Iʿrāb tool and for admin word-structure
                        generation. Iʿrāb has a model floor — see docs/WORD_STRUCTURE.md.
                      </p>
                      <div className="flex items-center justify-between">
                        <label className="text-sm text-slate-600">Iʿrāb sentences / day</label>
                        <input
                          type="number" min="1" max="500" inputMode="numeric"
                          value={limits.max_daily_morphology_sentences ?? 20}
                          onChange={(e) => setLimits((l) => ({ ...l, max_daily_morphology_sentences: parseInt(e.target.value) || 1 }))}
                          className="w-20 px-2 py-1.5 rounded-lg border border-slate-200 text-base text-center focus:outline-none focus:ring-2 focus:ring-[var(--gold)]"
                        />
                      </div>
                      <div className="flex items-center justify-between">
                        <label className="text-sm text-slate-600">Iʿrāb requests per IP / hour (0 = off)</label>
                        <input
                          type="number" min="0" max="1000" inputMode="numeric"
                          value={limits.morphology_ip_hourly_limit ?? 30}
                          onChange={(e) => setLimits((l) => ({ ...l, morphology_ip_hourly_limit: Math.max(0, parseInt(e.target.value) || 0) }))}
                          className="w-20 px-2 py-1.5 rounded-lg border border-slate-200 text-base text-center focus:outline-none focus:ring-2 focus:ring-[var(--gold)]"
                        />
                      </div>
                      <div className="flex items-center justify-between">
                        <label className="text-sm text-slate-600">Iʿrāb requests per user / hour (0 = off)</label>
                        <input
                          type="number" min="0" max="1000" inputMode="numeric"
                          value={limits.morphology_user_hourly_limit ?? 20}
                          onChange={(e) => setLimits((l) => ({ ...l, morphology_user_hourly_limit: Math.max(0, parseInt(e.target.value) || 0) }))}
                          className="w-20 px-2 py-1.5 rounded-lg border border-slate-200 text-base text-center focus:outline-none focus:ring-2 focus:ring-[var(--gold)]"
                        />
                      </div>
                      <div className="flex items-center justify-between">
                        <label className="text-sm text-slate-600">Iʿrāb max sentence length (chars)</label>
                        <input
                          type="number" min="20" max="2000" inputMode="numeric"
                          value={limits.morphology_max_chars ?? 200}
                          onChange={(e) => setLimits((l) => ({ ...l, morphology_max_chars: parseInt(e.target.value) || 20 }))}
                          className="w-20 px-2 py-1.5 rounded-lg border border-slate-200 text-base text-center focus:outline-none focus:ring-2 focus:ring-[var(--gold)]"
                        />
                      </div>
                      <div className="flex items-center justify-between">
                        <label className="text-sm text-slate-600">FREE tier: sentences / day (0 = none)</label>
                        <input
                          type="number" min="0" max="100" inputMode="numeric"
                          value={limits.morphology_free_daily_sentences ?? 5}
                          onChange={(e) => setLimits((l) => ({ ...l, morphology_free_daily_sentences: Math.max(0, parseInt(e.target.value) || 0) }))}
                          className="w-20 px-2 py-1.5 rounded-lg border border-slate-200 text-base text-center focus:outline-none focus:ring-2 focus:ring-[var(--gold)]"
                        />
                      </div>
                      <div className="flex items-center justify-between">
                        <label className="text-sm text-slate-600">FREE tier: AI-refined sentences / day (0 = none)</label>
                        <input
                          type="number" min="0" max="100" inputMode="numeric"
                          value={limits.morphology_free_refine_daily ?? 5}
                          onChange={(e) => setLimits((l) => ({ ...l, morphology_free_refine_daily: Math.max(0, parseInt(e.target.value) || 0) }))}
                          className="w-20 px-2 py-1.5 rounded-lg border border-slate-200 text-base text-center focus:outline-none focus:ring-2 focus:ring-[var(--gold)]"
                        />
                      </div>
                      <div className="flex items-center justify-between">
                        <label className="text-sm text-slate-600">
                          FREE tier: AI refinement enabled
                          <span className="block text-xs text-slate-400">Kill switch — off stops free-tier AI spend immediately.</span>
                        </label>
                        <input
                          type="checkbox"
                          checked={limits.morphology_free_refine_enabled ?? true}
                          onChange={(e) => setLimits((l) => ({ ...l, morphology_free_refine_enabled: e.target.checked }))}
                        />
                      </div>
                      {/* ── AI Topic Quiz (journey step 5, docs/AI_TOPIC_QUIZ.md) ── */}
                      <p className="text-xs font-semibold uppercase tracking-wide text-slate-400 pt-2">AI Topic Quiz</p>
                      <div className="flex items-center justify-between">
                        <label className="text-sm text-slate-600">
                          AI quiz enabled for learners (launch switch)
                          <span className="block text-xs text-slate-400">Staff always get it; off = learners keep the legacy quiz.</span>
                        </label>
                        <input
                          type="checkbox"
                          checked={limits.topic_quiz_ai_enabled ?? false}
                          onChange={(e) => setLimits((l) => ({ ...l, topic_quiz_ai_enabled: e.target.checked }))}
                        />
                      </div>
                      <div className="flex items-center justify-between">
                        <label className="text-sm text-slate-600">Quiz question-bank model</label>
                        <select
                          value={limits.topic_quiz_ai_model ?? ""}
                          onChange={(e) => setLimits((l) => ({ ...l, topic_quiz_ai_model: e.target.value }))}
                          className="px-2 py-1.5 rounded-lg border border-slate-200 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-[var(--gold)]"
                        >
                          <option value="">Same as AI model</option>
                          <option value="gpt-5">GPT-5</option>
                          <option value="gpt-5-mini">GPT-5 Mini</option>
                          <option value="gpt-5-nano">GPT-5 Nano</option>
                          <option value="gpt-4.1">GPT-4.1</option>
                          <option value="gpt-4.1-mini">GPT-4.1 Mini</option>
                          <option value="gpt-4o">GPT-4o</option>
                          <option value="gpt-4o-mini">GPT-4o Mini</option>
                          <option value="gpt-4-turbo">GPT-4 Turbo</option>
                          <option value="gpt-4">GPT-4</option>
                          <option value="gpt-3.5-turbo">GPT-3.5 Turbo</option>
                        </select>
                      </div>
                      <div className="flex items-center justify-between">
                        <label className="text-sm text-slate-600">
                          Reasoning effort (GPT-5 models only)
                          <span className="block text-xs text-slate-400">Low ≈ 30 s per bank; medium ≈ 2× slower, more careful Arabic. Regenerate a topic to apply.</span>
                        </label>
                        <select
                          value={limits.topic_quiz_reasoning_effort ?? "low"}
                          onChange={(e) => setLimits((l) => ({ ...l, topic_quiz_reasoning_effort: e.target.value }))}
                          className="px-2 py-1.5 rounded-lg border border-slate-200 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-[var(--gold)]"
                        >
                          <option value="minimal">Minimal</option>
                          <option value="low">Low (default)</option>
                          <option value="medium">Medium</option>
                          <option value="high">High</option>
                        </select>
                      </div>
                      <div className="flex items-center justify-between">
                        <label className="text-sm text-slate-600">
                          Learner-created topics: model
                          <span className="block text-xs text-slate-400">Personal (unpublished) topics bill the learner; a cheaper model saves their allowance.</span>
                        </label>
                        <select
                          value={limits.topic_quiz_personal_ai_model ?? ""}
                          onChange={(e) => setLimits((l) => ({ ...l, topic_quiz_personal_ai_model: e.target.value }))}
                          className="px-2 py-1.5 rounded-lg border border-slate-200 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-[var(--gold)]"
                        >
                          <option value="">Same as curriculum topics</option>
                          <option value="gpt-5">GPT-5</option>
                          <option value="gpt-5-mini">GPT-5 Mini</option>
                          <option value="gpt-5-nano">GPT-5 Nano</option>
                          <option value="gpt-4.1">GPT-4.1</option>
                          <option value="gpt-4.1-mini">GPT-4.1 Mini</option>
                          <option value="gpt-4o">GPT-4o</option>
                          <option value="gpt-4o-mini">GPT-4o Mini</option>
                          <option value="gpt-4-turbo">GPT-4 Turbo</option>
                          <option value="gpt-4">GPT-4</option>
                          <option value="gpt-3.5-turbo">GPT-3.5 Turbo</option>
                        </select>
                      </div>
                      <div className="flex items-center justify-between">
                        <label className="text-sm text-slate-600">Learner-created topics: reasoning effort</label>
                        <select
                          value={limits.topic_quiz_personal_reasoning_effort ?? ""}
                          onChange={(e) => setLimits((l) => ({ ...l, topic_quiz_personal_reasoning_effort: e.target.value }))}
                          className="px-2 py-1.5 rounded-lg border border-slate-200 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-[var(--gold)]"
                        >
                          <option value="">Same as curriculum topics</option>
                          <option value="minimal">Minimal</option>
                          <option value="low">Low</option>
                          <option value="medium">Medium</option>
                          <option value="high">High</option>
                        </select>
                      </div>
                      <div className="flex items-center justify-between">
                        <label className="text-sm text-slate-600">Questions per attempt</label>
                        <input
                          type="number" min={4} max={40}
                          value={limits.topic_quiz_questions_per_attempt ?? 14}
                          onChange={(e) => setLimits((l) => ({ ...l, topic_quiz_questions_per_attempt: parseInt(e.target.value) || 14 }))}
                          className="w-20 px-2 py-1.5 rounded-lg border border-slate-200 bg-white text-sm"
                        />
                      </div>
                      <div className="flex items-center justify-between">
                        <label className="text-sm text-slate-600">
                          Max question batches per topic
                          <span className="block text-xs text-slate-400">Extra batches are written only when a learner has seen most questions.</span>
                        </label>
                        <input
                          type="number" min={1} max={10}
                          value={limits.topic_quiz_max_batches ?? 3}
                          onChange={(e) => setLimits((l) => ({ ...l, topic_quiz_max_batches: parseInt(e.target.value) || 1 }))}
                          className="w-20 px-2 py-1.5 rounded-lg border border-slate-200 bg-white text-sm"
                        />
                      </div>
                      {/* ── Verb Conjugator (full paradigm for any verb form) ── */}
                      <p className="text-xs font-semibold uppercase tracking-wide text-slate-400 pt-2">Verb Conjugator</p>
                      <div className="flex items-center justify-between">
                        <label className="text-sm text-slate-600">Conjugator enabled (launch switch)</label>
                        <input
                          type="checkbox"
                          checked={limits.conjugator_tool_enabled ?? false}
                          onChange={(e) => setLimits((l) => ({ ...l, conjugator_tool_enabled: e.target.checked }))}
                        />
                      </div>
                      <div className="flex items-center justify-between">
                        <label className="text-sm text-slate-600">Conjugator AI model</label>
                        <select
                          value={limits.conjugator_ai_model ?? ""}
                          onChange={(e) => setLimits((l) => ({ ...l, conjugator_ai_model: e.target.value }))}
                          className="px-2 py-1.5 rounded-lg border border-slate-200 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-[var(--gold)]"
                        >
                          <option value="">Same as AI model</option>
                          <option value="gpt-5">GPT-5</option>
                          <option value="gpt-5-mini">GPT-5 Mini</option>
                          <option value="gpt-5-nano">GPT-5 Nano</option>
                          <option value="gpt-4.1">GPT-4.1</option>
                          <option value="gpt-4.1-mini">GPT-4.1 Mini</option>
                          <option value="gpt-4o">GPT-4o</option>
                          <option value="gpt-4o-mini">GPT-4o Mini</option>
                          <option value="gpt-4-turbo">GPT-4 Turbo</option>
                          <option value="gpt-4">GPT-4</option>
                          <option value="gpt-3.5-turbo">GPT-3.5 Turbo</option>
                        </select>
                      </div>
                      <p className="text-xs text-slate-400 -mt-1">
                        Cache hits are free for everyone — only NEW verbs cost an AI call
                        and count against the daily lookups below.
                      </p>
                      <div className="flex items-center justify-between">
                        <label className="text-sm text-slate-600">Conjugator new verbs / day (subscribers)</label>
                        <input
                          type="number" min="1" max="500" inputMode="numeric"
                          value={limits.max_daily_conjugator_lookups ?? 30}
                          onChange={(e) => setLimits((l) => ({ ...l, max_daily_conjugator_lookups: parseInt(e.target.value) || 1 }))}
                          className="w-20 px-2 py-1.5 rounded-lg border border-slate-200 text-base text-center focus:outline-none focus:ring-2 focus:ring-[var(--gold)]"
                        />
                      </div>
                      <div className="flex items-center justify-between">
                        <label className="text-sm text-slate-600">Conjugator requests per IP / hour (0 = off)</label>
                        <input
                          type="number" min="0" max="1000" inputMode="numeric"
                          value={limits.conjugator_ip_hourly_limit ?? 30}
                          onChange={(e) => setLimits((l) => ({ ...l, conjugator_ip_hourly_limit: Math.max(0, parseInt(e.target.value) || 0) }))}
                          className="w-20 px-2 py-1.5 rounded-lg border border-slate-200 text-base text-center focus:outline-none focus:ring-2 focus:ring-[var(--gold)]"
                        />
                      </div>
                      <div className="flex items-center justify-between">
                        <label className="text-sm text-slate-600">Conjugator requests per user / hour (0 = off)</label>
                        <input
                          type="number" min="0" max="1000" inputMode="numeric"
                          value={limits.conjugator_user_hourly_limit ?? 20}
                          onChange={(e) => setLimits((l) => ({ ...l, conjugator_user_hourly_limit: Math.max(0, parseInt(e.target.value) || 0) }))}
                          className="w-20 px-2 py-1.5 rounded-lg border border-slate-200 text-base text-center focus:outline-none focus:ring-2 focus:ring-[var(--gold)]"
                        />
                      </div>
                      <div className="flex items-center justify-between">
                        <label className="text-sm text-slate-600">Conjugator max word length (chars)</label>
                        <input
                          type="number" min="5" max="100" inputMode="numeric"
                          value={limits.conjugator_max_chars ?? 30}
                          onChange={(e) => setLimits((l) => ({ ...l, conjugator_max_chars: parseInt(e.target.value) || 5 }))}
                          className="w-20 px-2 py-1.5 rounded-lg border border-slate-200 text-base text-center focus:outline-none focus:ring-2 focus:ring-[var(--gold)]"
                        />
                      </div>
                      <div className="flex items-center justify-between">
                        <label className="text-sm text-slate-600">FREE tier: new verbs / day (0 = cache hits only)</label>
                        <input
                          type="number" min="0" max="100" inputMode="numeric"
                          value={limits.conjugator_free_daily_lookups ?? 5}
                          onChange={(e) => setLimits((l) => ({ ...l, conjugator_free_daily_lookups: Math.max(0, parseInt(e.target.value) || 0) }))}
                          className="w-20 px-2 py-1.5 rounded-lg border border-slate-200 text-base text-center focus:outline-none focus:ring-2 focus:ring-[var(--gold)]"
                        />
                      </div>

                      {/* ── Typing tutor (public /typing page) ── */}
                      <p className="text-xs font-semibold uppercase tracking-wide text-slate-400 pt-2">Typing Tutor</p>
                      <div className="flex items-center justify-between">
                        <label className="text-sm text-slate-600">
                          Public typing tutor enabled (launch switch)
                          <span className="block text-xs text-slate-400">Signed-in users keep the in-app tutor either way.</span>
                        </label>
                        <input
                          type="checkbox"
                          checked={limits.typing_tool_enabled ?? false}
                          onChange={(e) => setLimits((l) => ({ ...l, typing_tool_enabled: e.target.checked }))}
                        />
                      </div>
                      <div className="flex items-center justify-between">
                        <label className="text-sm text-slate-600">Typing reads per anonymous IP / hour (0 = off)</label>
                        <input
                          type="number" min="0" max="5000" inputMode="numeric"
                          value={limits.typing_ip_hourly_limit ?? 300}
                          onChange={(e) => setLimits((l) => ({ ...l, typing_ip_hourly_limit: Math.max(0, parseInt(e.target.value) || 0) }))}
                          className="w-20 px-2 py-1.5 rounded-lg border border-slate-200 text-base text-center focus:outline-none focus:ring-2 focus:ring-[var(--gold)]"
                        />
                      </div>
                      {/* ── PDF Reader (public /reader page) ── */}
                      <p className="text-xs font-semibold uppercase tracking-wide text-slate-400 pt-2">PDF Reader</p>
                      <div className="flex items-center justify-between">
                        <label className="text-sm text-slate-600">
                          Public PDF reader enabled (launch switch)
                          <span className="block text-xs text-slate-400">Subscribers keep the in-app reader either way; the flag adds the free tier.</span>
                        </label>
                        <input
                          type="checkbox"
                          checked={limits.reading_tool_enabled ?? false}
                          onChange={(e) => setLimits((l) => ({ ...l, reading_tool_enabled: e.target.checked }))}
                        />
                      </div>
                      <div className="flex items-center justify-between">
                        <label className="text-sm text-slate-600">FREE tier: OCR pages / day (0 = none)</label>
                        <input
                          type="number" min="0" max="500" inputMode="numeric"
                          value={limits.reading_free_daily_ocr_pages ?? 10}
                          onChange={(e) => setLimits((l) => ({ ...l, reading_free_daily_ocr_pages: Math.max(0, parseInt(e.target.value) || 0) }))}
                          className="w-20 px-2 py-1.5 rounded-lg border border-slate-200 text-base text-center focus:outline-none focus:ring-2 focus:ring-[var(--gold)]"
                        />
                      </div>
                      <div className="flex items-center justify-between">
                        <label className="text-sm text-slate-600">FREE tier: new translations / day (0 = cache hits only)</label>
                        <input
                          type="number" min="0" max="500" inputMode="numeric"
                          value={limits.reading_free_daily_translations ?? 5}
                          onChange={(e) => setLimits((l) => ({ ...l, reading_free_daily_translations: Math.max(0, parseInt(e.target.value) || 0) }))}
                          className="w-20 px-2 py-1.5 rounded-lg border border-slate-200 text-base text-center focus:outline-none focus:ring-2 focus:ring-[var(--gold)]"
                        />
                      </div>
                      <div className="flex items-center justify-between">
                        <label className="text-sm text-slate-600">FREE tier: new word glosses / day (0 = cache hits only)</label>
                        <input
                          type="number" min="0" max="500" inputMode="numeric"
                          value={limits.reading_free_daily_word_glosses ?? 10}
                          onChange={(e) => setLimits((l) => ({ ...l, reading_free_daily_word_glosses: Math.max(0, parseInt(e.target.value) || 0) }))}
                          className="w-20 px-2 py-1.5 rounded-lg border border-slate-200 text-base text-center focus:outline-none focus:ring-2 focus:ring-[var(--gold)]"
                        />
                      </div>
                      <div className="flex items-center justify-between">
                        <label className="text-sm text-slate-600">Reader requests per anonymous IP / hour (0 = off)</label>
                        <input
                          type="number" min="0" max="5000" inputMode="numeric"
                          value={limits.reading_ip_hourly_limit ?? 120}
                          onChange={(e) => setLimits((l) => ({ ...l, reading_ip_hourly_limit: Math.max(0, parseInt(e.target.value) || 0) }))}
                          className="w-20 px-2 py-1.5 rounded-lg border border-slate-200 text-base text-center focus:outline-none focus:ring-2 focus:ring-[var(--gold)]"
                        />
                      </div>

                      <div className="flex items-center justify-between">
                        <label className="text-sm text-slate-600">User video transcribe limit (sec)</label>
                        <input
                          type="number" min="10" max="3600" inputMode="numeric"
                          value={limits.user_video_max_seconds ?? 60}
                          onChange={(e) => setLimits((l) => ({ ...l, user_video_max_seconds: parseInt(e.target.value) || 10 }))}
                          className="w-20 px-2 py-1.5 rounded-lg border border-slate-200 text-base text-center focus:outline-none focus:ring-2 focus:ring-[var(--gold)]"
                        />
                      </div>
                      <label className="flex items-center justify-between cursor-pointer">
                        <span className="text-sm text-slate-600">Reading: remember last page</span>
                        <input
                          type="checkbox"
                          checked={limits.reading_remember_page ?? true}
                          onChange={(e) => setLimits((l) => ({ ...l, reading_remember_page: e.target.checked }))}
                          className="w-4 h-4"
                        />
                      </label>
                      <label className="flex items-center justify-between cursor-pointer">
                        <span className="text-sm text-slate-600">Reading: high-res OCR (better scans)</span>
                        <input
                          type="checkbox"
                          checked={limits.reading_ocr_high_res ?? true}
                          onChange={(e) => setLimits((l) => ({ ...l, reading_ocr_high_res: e.target.checked }))}
                          className="w-4 h-4"
                        />
                      </label>
                      <label className="flex items-start justify-between cursor-pointer gap-3">
                        <span className="text-sm text-slate-600">
                          Call auto-suggest <span className="text-dt-gold-dim">(experimental)</span>
                          <span className="block text-xs text-slate-400">Live-transcribe the partner & suggest replies. Adds STT + AI cost.</span>
                        </span>
                        <input
                          type="checkbox"
                          checked={limits.auto_suggest_enabled ?? false}
                          onChange={(e) => setLimits((l) => ({ ...l, auto_suggest_enabled: e.target.checked }))}
                          className="w-4 h-4 mt-0.5 shrink-0"
                        />
                      </label>
                      {(limits.auto_suggest_enabled ?? false) && (
                        <div className="flex items-center justify-between pl-1">
                          <label className="text-sm text-slate-600">Auto-suggest cycles / day</label>
                          <input
                            type="number" min="1" max="1000" inputMode="numeric"
                            value={limits.max_daily_auto_suggest ?? 100}
                            onChange={(e) => setLimits((l) => ({ ...l, max_daily_auto_suggest: parseInt(e.target.value) || 1 }))}
                            className="w-20 px-2 py-1.5 rounded-lg border border-slate-200 text-base text-center focus:outline-none focus:ring-2 focus:ring-[var(--gold)]"
                          />
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Subscriptions */}
                  <div>
                    <h3 className="text-sm font-medium text-slate-700 mb-3">Subscriptions</h3>
                    <div className="space-y-3">
                      <label className="flex items-start justify-between cursor-pointer gap-3">
                        <span className="text-sm text-slate-600">
                          Require subscription to sign up
                          <span className="block text-xs text-slate-400">New accounts must start a paid Stripe subscription. Off = paywall disabled for everyone.</span>
                        </span>
                        <input
                          type="checkbox"
                          checked={limits.subscription_required ?? true}
                          onChange={(e) => setLimits((l) => ({ ...l, subscription_required: e.target.checked }))}
                          className="w-4 h-4 mt-0.5 shrink-0"
                        />
                      </label>
                      <div className="flex items-center justify-between">
                        <label className="text-sm text-slate-600">Free trial length (days)</label>
                        <input
                          type="number" min="0" max="365" inputMode="numeric"
                          value={limits.trial_period_days ?? 7}
                          onChange={(e) => setLimits((l) => ({ ...l, trial_period_days: parseInt(e.target.value) || 0 }))}
                          className="w-20 px-2 py-1.5 rounded-lg border border-slate-200 text-base text-center focus:outline-none focus:ring-2 focus:ring-[var(--gold)]"
                        />
                      </div>
                      <div className="flex items-center justify-between">
                        <label className="text-sm text-slate-600">
                          Monthly price (US cents)
                          <span className="block text-xs text-slate-400">
                            {Number.isFinite(limits.individual_monthly_price_cents)
                              ? `$${(limits.individual_monthly_price_cents / 100).toFixed(2)}/month. `
                              : ""}
                            Applies to new checkouts only — existing subscribers keep their price.
                          </span>
                        </label>
                        <input
                          type="number" min="1" max="100000" inputMode="numeric"
                          value={limits.individual_monthly_price_cents ?? 2000}
                          onChange={(e) => setLimits((l) => ({ ...l, individual_monthly_price_cents: parseInt(e.target.value) || 1 }))}
                          className="w-24 px-2 py-1.5 rounded-lg border border-slate-200 text-base text-center focus:outline-none focus:ring-2 focus:ring-[var(--gold)]"
                        />
                      </div>
                      <div className="flex items-center justify-between">
                        <label className="text-sm text-slate-600">
                          Yearly plan monthly rate (US cents)
                          <span className="block text-xs text-slate-400">
                            {Number.isFinite(limits.individual_yearly_monthly_price_cents)
                              ? `$${(limits.individual_yearly_monthly_price_cents / 100).toFixed(2)}/month — billed yearly up front ($${((limits.individual_yearly_monthly_price_cents * 12) / 100).toFixed(2)}/year). `
                              : ""}
                            Applies to new checkouts only — existing subscribers keep their price.
                          </span>
                        </label>
                        <input
                          type="number" min="1" max="100000" inputMode="numeric"
                          value={limits.individual_yearly_monthly_price_cents ?? 1799}
                          onChange={(e) => setLimits((l) => ({ ...l, individual_yearly_monthly_price_cents: parseInt(e.target.value) || 1 }))}
                          className="w-24 px-2 py-1.5 rounded-lg border border-slate-200 text-base text-center focus:outline-none focus:ring-2 focus:ring-[var(--gold)]"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Institutes */}
                  <div>
                    <h3 className="text-sm font-medium text-slate-700 mb-3">Institutes</h3>
                    <div className="space-y-3">
                      <label className="flex items-start justify-between cursor-pointer gap-3">
                        <span className="text-sm text-slate-600">
                          Allow institute registration
                          <span className="block text-xs text-slate-400">
                            Shows the Institute account type on signup and accepts new institute
                            checkouts. Off = hidden while the feature is being finished; existing
                            institutes are unaffected.
                          </span>
                        </span>
                        <input
                          type="checkbox"
                          checked={limits.institute_signup_enabled ?? false}
                          onChange={(e) => setLimits((l) => ({ ...l, institute_signup_enabled: e.target.checked }))}
                          className="w-4 h-4 mt-0.5 shrink-0"
                        />
                      </label>
                      <div className="flex items-center justify-between">
                        <label className="text-sm text-slate-600">
                          Minimum seats
                          <span className="block text-xs text-slate-400">Fewest seats an institute may purchase.</span>
                        </label>
                        <input
                          type="number" min="1" max="1000" inputMode="numeric"
                          value={limits.institute_min_seats ?? 10}
                          onChange={(e) => setLimits((l) => ({ ...l, institute_min_seats: parseInt(e.target.value) || 1 }))}
                          className="w-20 px-2 py-1.5 rounded-lg border border-slate-200 text-base text-center focus:outline-none focus:ring-2 focus:ring-[var(--gold)]"
                        />
                      </div>
                      <div className="flex items-center justify-between">
                        <label className="text-sm text-slate-600">
                          Monthly seat price (US cents)
                          <span className="block text-xs text-slate-400">
                            {Number.isFinite(limits.institute_seat_monthly_price_cents)
                              ? `$${(limits.institute_seat_monthly_price_cents / 100).toFixed(2)}/month per seat — billed yearly up front ($${((limits.institute_seat_monthly_price_cents * 12) / 100).toFixed(2)}/seat/year). `
                              : ""}
                            Applies to new checkouts only.
                          </span>
                        </label>
                        <input
                          type="number" min="1" max="100000" inputMode="numeric"
                          value={limits.institute_seat_monthly_price_cents ?? 1500}
                          onChange={(e) => setLimits((l) => ({ ...l, institute_seat_monthly_price_cents: parseInt(e.target.value) || 1 }))}
                          className="w-24 px-2 py-1.5 rounded-lg border border-slate-200 text-base text-center focus:outline-none focus:ring-2 focus:ring-[var(--gold)]"
                        />
                      </div>
                    </div>
                  </div>

                </>
              )}
            </div>
          )}

          {activeTab === "institutes" && <AdminInstitutesPanel />}
          {activeTab === "groups" && <AdminCategoryGroupsPanel />}
          {activeTab === "vouchers" && <AdminVouchersPanel />}
          {activeTab === "costs" && <AdminCostsPanel />}
          {activeTab === "quiz" && <AdminQuizEditor />}
          {activeTab === "practice" && <AdminPracticeCallsPanel />}
          {activeTab === "quran" && <AdminQuranImportPanel />}
        </div>

        {/* Footer — only on settings tab */}
        {activeTab === "settings" && (
          <div className="px-6 py-4 border-t border-slate-100 flex items-center justify-between gap-3 shrink-0">
            {saveMsg && <span className="text-sm text-emerald-600">{saveMsg}</span>}
            {!saveMsg && <span />}
            <div className="flex gap-2">
              <button
                onClick={onClose}
                className="px-4 py-2 rounded-lg border border-slate-200 text-sm hover:bg-slate-50 text-slate-700"
              >
                Close
              </button>
              <button
                onClick={handleSave}
                disabled={saving || loading}
                className="px-4 py-2 rounded-lg bg-dt-gold-bright text-dt-ink text-sm font-semibold hover:brightness-105 disabled:opacity-50 transition-[filter]"
              >
                {saving ? "Saving…" : "Save Settings"}
              </button>
            </div>
          </div>
        )}
        {activeTab === "vouchers" && (
          <div className="px-6 py-4 border-t border-slate-100 flex justify-end shrink-0">
            <button onClick={onClose} className="px-4 py-2 rounded-lg border border-slate-200 text-sm hover:bg-slate-50 text-slate-700">
              Close
            </button>
          </div>
        )}
        {activeTab === "quiz" && (
          <div className="px-6 py-4 border-t border-slate-100 flex justify-end shrink-0">
            <button onClick={onClose} className="px-4 py-2 rounded-lg border border-slate-200 text-sm hover:bg-slate-50 text-slate-700">
              Close
            </button>
          </div>
        )}
        {activeTab === "practice" && (
          <div className="px-6 py-4 border-t border-slate-100 flex justify-end shrink-0">
            <button onClick={onClose} className="px-4 py-2 rounded-lg border border-slate-200 text-sm hover:bg-slate-50 text-slate-700">
              Close
            </button>
          </div>
        )}
        {activeTab === "quran" && (
          <div className="px-6 py-4 border-t border-slate-100 flex justify-end shrink-0">
            <button onClick={onClose} className="px-4 py-2 rounded-lg border border-slate-200 text-sm hover:bg-slate-50 text-slate-700">
              Close
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

function AdminInstitutesPanel() {
  const [institutes, setInstitutes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [busyId, setBusyId] = useState(null);

  const load = () => {
    setLoading(true);
    adminListInstitutes()
      .then((d) => setInstitutes(d.institutes || []))
      .catch(() => setError("Failed to load institutes"))
      .finally(() => setLoading(false));
  };
  useEffect(load, []);

  const act = async (inst, fn, confirmMsg) => {
    if (confirmMsg && !window.confirm(confirmMsg)) return;
    setError(null);
    setBusyId(inst.id);
    try {
      const { institute } = await fn(inst.id);
      setInstitutes((list) => list.map((x) => (x.id === institute.id ? institute : x)));
    } catch (err) {
      setError(err.message || "Action failed.");
    } finally {
      setBusyId(null);
    }
  };

  const handleDelete = async (inst) => {
    const typed = window.prompt(
      `Permanently delete "${inst.name}"?\n\nThis deletes the institute, ALL of its student/teacher accounts, and their topics and progress. Ex-students who switched to their own subscription are kept.\n\nType the institute's exact name to confirm:`
    );
    if (typed === null) return;
    setError(null);
    setBusyId(inst.id);
    try {
      await adminDeleteInstitute(inst.id, typed.trim());
      setInstitutes((list) => list.filter((x) => x.id !== inst.id));
    } catch (err) {
      setError(err.message || "Could not delete the institute.");
    } finally {
      setBusyId(null);
    }
  };

  const statusBadge = (inst) => {
    if (inst.status === "pending")
      return <span className="text-xs font-medium px-1.5 py-0.5 rounded bg-amber-100 text-amber-700">Pending</span>;
    if (inst.status === "disabled")
      return <span className="text-xs font-medium px-1.5 py-0.5 rounded bg-rose-100 text-rose-700">Disabled</span>;
    if (!inst.billing_ok)
      return <span className="text-xs font-medium px-1.5 py-0.5 rounded bg-orange-100 text-orange-700">Payment lapsed</span>;
    return <span className="text-xs font-medium px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-700">Active</span>;
  };

  return (
    <div className="space-y-4">
      <p className="text-xs text-slate-400">
        Institutes register and pay for their seats themselves; approve them here to let them
        add students and teachers. Disabling an institute blocks access for all of its members
        (students may then continue with their own individual subscription).
      </p>
      {loading && <p className="text-sm text-slate-500">Loading institutes…</p>}
      {error && <p className="text-sm text-red-600">{error}</p>}
      {!loading && institutes.length === 0 && (
        <p className="text-sm text-slate-500">No institutes yet.</p>
      )}
      <div className="space-y-3">
        {institutes.map((inst) => (
          <div key={inst.id} className="rounded-xl border border-slate-200 p-4">
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-semibold text-slate-800 truncate">{inst.name}</span>
                  {statusBadge(inst)}
                </div>
                <p className="text-xs text-slate-500 mt-1 truncate">
                  {inst.admin_name} · {inst.admin_email}
                </p>
                <p className="text-xs text-slate-400 mt-1">
                  Seats: {inst.seats_used}/{inst.seats_purchased}
                  {inst.subscription_status && <> · Stripe: {inst.subscription_status}</>}
                  {inst.current_period_end && (
                    <> · Renews {new Date(inst.current_period_end).toLocaleDateString()}</>
                  )}
                </p>
              </div>
              <div className="flex flex-col gap-1.5 shrink-0">
                {inst.status !== "active" && (
                  <button
                    onClick={() => act(inst, adminApproveInstitute)}
                    disabled={busyId === inst.id}
                    className="px-3 py-1.5 rounded-lg bg-emerald-600 text-white text-xs font-semibold hover:bg-emerald-700 disabled:opacity-50"
                  >
                    {inst.status === "disabled" ? "Re-enable" : "Approve"}
                  </button>
                )}
                {inst.status === "active" && (
                  <button
                    onClick={() =>
                      act(
                        inst,
                        adminDisableInstitute,
                        `Disable ${inst.name}? All of its students and teachers will lose access until it is re-enabled (or they subscribe individually).`
                      )
                    }
                    disabled={busyId === inst.id}
                    className="px-3 py-1.5 rounded-lg border border-rose-200 text-rose-600 text-xs font-semibold hover:bg-rose-50 disabled:opacity-50"
                  >
                    Disable
                  </button>
                )}
                <button
                  onClick={() => handleDelete(inst)}
                  disabled={busyId === inst.id}
                  className="px-3 py-1.5 rounded-lg text-rose-500 text-xs font-medium hover:bg-rose-50 disabled:opacity-50"
                >
                  Delete…
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}


function AdminVouchersPanel() {
  const [vouchers, setVouchers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const tenDaysFromNow = () => {
    const d = new Date();
    d.setDate(d.getDate() + 10);
    return d.toISOString().slice(0, 10);
  };

  const [form, setForm] = useState({ label: "", access_days: 180, redeem_by: tenDaysFromNow(), max_redemptions: 1, code: "" });
  const [creating, setCreating] = useState(false);
  const [createMsg, setCreateMsg] = useState(null);
  const [copiedId, setCopiedId] = useState(null);

  const load = () => {
    setLoading(true);
    adminListVouchers()
      .then((d) => setVouchers(d.vouchers || []))
      .catch(() => setError("Failed to load vouchers"))
      .finally(() => setLoading(false));
  };
  useEffect(load, []);

  const handleCreate = async (e) => {
    e.preventDefault();
    setCreateMsg(null);
    setError(null);
    if (!form.access_days || form.access_days < 1) { setError("Enter how many days of access the voucher grants."); return; }
    setCreating(true);
    try {
      const created = await adminCreateVoucher({
        label: form.label.trim(),
        access_days: parseInt(form.access_days, 10),
        redeem_by: form.redeem_by,
        max_redemptions: parseInt(form.max_redemptions, 10) || 1,
        code: form.code.trim().toUpperCase() || undefined,
      });
      setVouchers((v) => [created, ...v]);
      setCreateMsg(`Voucher ${created.code} created.`);
      setForm((f) => ({ ...f, label: "", code: "" }));
    } catch (err) {
      setError(err.message || "Could not create the voucher.");
    } finally {
      setCreating(false);
    }
  };

  const handleRevoke = async (v) => {
    const used = v.redemption_count > 0;
    const msg = used
      ? `Revoke ${v.code}? It can no longer be redeemed, and access already granted to ${v.redemption_count} ${v.redemption_count === 1 ? "person" : "people"} will be withdrawn.`
      : `Revoke ${v.code}? It can no longer be redeemed.`;
    if (!window.confirm(msg)) return;
    setError(null);
    try {
      const updated = await adminRevokeVoucher(v.id);
      setVouchers((vs) => vs.map((x) => (x.id === v.id ? updated : x)));
    } catch (err) {
      setError(err.message || "Could not revoke the voucher.");
    }
  };

  const copyCode = (v) => {
    navigator.clipboard?.writeText(v.code).then(() => {
      setCopiedId(v.id);
      setTimeout(() => setCopiedId((c) => (c === v.id ? null : c)), 1500);
    });
  };

  const stateBadge = (state) => {
    const map = {
      active: "bg-emerald-100 text-emerald-700",
      used: "bg-slate-200 text-slate-600",
      expired: "bg-amber-100 text-amber-700",
      revoked: "bg-rose-100 text-rose-700",
    };
    return map[state] || "bg-slate-100 text-slate-600";
  };

  const fmtDate = (iso) => {
    const d = new Date(iso);
    return isNaN(d) ? "—" : d.toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" });
  };

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-sm font-medium text-slate-700 mb-1">Create a voucher</h3>
        <p className="text-xs text-slate-500 mb-3">
          Grants paywall-free access for a set number of days — no card needed. Share the code; the
          recipient enters it at sign-up or on the subscribe screen.
        </p>
        <form onSubmit={handleCreate} className="space-y-3 surface-glass rounded-xl p-4">
          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1">Label (optional)</label>
            <input
              type="text"
              value={form.label}
              onChange={(e) => setForm((f) => ({ ...f, label: e.target.value }))}
              placeholder="e.g. Mum's account"
              className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-[var(--gold)]"
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">Access granted (days)</label>
              <input
                type="number" min="1"
                value={form.access_days}
                onChange={(e) => setForm((f) => ({ ...f, access_days: e.target.value }))}
                className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-[var(--gold)]"
              />
              <p className="text-[11px] text-slate-400 mt-1">180 ≈ 6 months</p>
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">Redeem by</label>
              <input
                type="date"
                value={form.redeem_by}
                onChange={(e) => setForm((f) => ({ ...f, redeem_by: e.target.value }))}
                className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-[var(--gold)]"
              />
              <p className="text-[11px] text-slate-400 mt-1">Expires if unused by this date</p>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">Max uses</label>
              <input
                type="number" min="1"
                value={form.max_redemptions}
                onChange={(e) => setForm((f) => ({ ...f, max_redemptions: e.target.value }))}
                className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-[var(--gold)]"
              />
              <p className="text-[11px] text-slate-400 mt-1">1 = single use</p>
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">Custom code (optional)</label>
              <input
                type="text"
                value={form.code}
                onChange={(e) => setForm((f) => ({ ...f, code: e.target.value.toUpperCase() }))}
                placeholder="Auto-generated"
                className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-[var(--gold)]"
              />
            </div>
          </div>
          {error && <p className="text-xs text-red-600">{error}</p>}
          {createMsg && <p className="text-xs text-emerald-600">{createMsg}</p>}
          <button
            type="submit"
            disabled={creating}
            className="w-full py-2 rounded-lg bg-dt-gold-bright text-dt-ink text-sm font-medium hover:brightness-105 disabled:opacity-50"
          >
            {creating ? "Creating…" : "Create voucher"}
          </button>
        </form>
      </div>

      <div>
        <h3 className="text-sm font-medium text-slate-700 mb-3">Vouchers</h3>
        {loading ? (
          <p className="text-sm text-slate-500">Loading…</p>
        ) : vouchers.length === 0 ? (
          <p className="text-sm text-slate-400">No vouchers yet.</p>
        ) : (
          <div className="space-y-2">
            {vouchers.map((v) => (
              <div key={v.id} className="surface-glass flex items-center gap-3 px-3 py-2.5 rounded-lg">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => copyCode(v)}
                      title="Copy code"
                      className="font-mono text-sm font-semibold text-slate-800 hover:text-dt-gold-bright"
                    >
                      {v.code}
                    </button>
                    {copiedId === v.id && <span className="text-[11px] text-emerald-600">copied</span>}
                    <span className={`text-[10px] font-semibold uppercase tracking-wide rounded-full px-1.5 py-0.5 ${stateBadge(v.state)}`}>{v.state}</span>
                  </div>
                  <p className="text-xs text-slate-500 truncate">
                    {v.label ? `${v.label} · ` : ""}{v.access_days}d access · {v.redemption_count}/{v.max_redemptions} used · redeem by {fmtDate(v.redeem_by)}
                  </p>
                </div>
                {v.is_active && (
                  <button
                    onClick={() => handleRevoke(v)}
                    className="text-xs text-rose-600 hover:text-rose-700 hover:underline shrink-0"
                  >
                    Revoke
                  </button>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function AdminPracticeCallsPanel() {
  // ── Matching settings ──
  const [limits, setLimits] = useState({ require_topic_completion_for_calls: true, min_practice_overlap_minutes: 15, practice_calls_disabled: false, practice_calls_button_hidden: false, practice_calls_require_18: false, practice_call_durations: [15, 30, 45, 60], max_weekly_calls: 2, max_call_participants: 2 });
  const [loadingLimits, setLoadingLimits] = useState(true);
  const [savingLimits, setSavingLimits] = useState(false);
  const [limitsMsg, setLimitsMsg] = useState(null);

  useEffect(() => {
    getDailyUsage()
      .then((usage) => {
        const l = usage.limits || {};
        setLimits({
          require_topic_completion_for_calls: l.require_topic_completion_for_calls ?? true,
          min_practice_overlap_minutes: l.min_practice_overlap_minutes ?? 15,
          practice_calls_disabled: l.practice_calls_disabled ?? false,
          practice_calls_button_hidden: l.practice_calls_button_hidden ?? false,
          practice_calls_require_18: l.practice_calls_require_18 ?? false,
          practice_call_durations: Array.isArray(l.practice_call_durations) && l.practice_call_durations.length ? l.practice_call_durations : [15, 30, 45, 60],
          max_weekly_calls: l.max_weekly_calls ?? 2,
          max_call_participants: l.max_call_participants ?? 2,
        });
      })
      .catch(() => {})
      .finally(() => setLoadingLimits(false));
  }, []);

  const toggleDuration = (d) => {
    setLimits((l) => {
      const cur = Array.isArray(l.practice_call_durations) ? l.practice_call_durations : [];
      const next = cur.includes(d) ? cur.filter((x) => x !== d) : [...cur, d];
      // Never allow an empty set — keep at least one duration selected.
      return { ...l, practice_call_durations: next.length ? next.sort((a, b) => a - b) : cur };
    });
  };

  const handleSaveLimits = async () => {
    setSavingLimits(true);
    setLimitsMsg(null);
    try {
      await setUsageLimits(limits);
      setLimitsMsg("Saved!");
      setTimeout(() => setLimitsMsg(null), 3000);
    } catch (e) {
      setLimitsMsg("Save failed");
    } finally {
      setSavingLimits(false);
    }
  };

  // ── User reset ──
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [resetMsg, setResetMsg] = useState({});
  const [resetting, setResetting] = useState({});
  const [dobEdits, setDobEdits] = useState({});
  const [dobMsg, setDobMsg] = useState({});
  const [dobSaving, setDobSaving] = useState({});

  const handleSearch = async (e) => {
    const q = e.target.value;
    setQuery(q);
    setResetMsg({});
    setDobMsg({});
    setDobEdits({});
    if (!q.trim()) { setResults([]); return; }
    setSearching(true);
    try {
      const data = await practiceAdminSearchUsers(q);
      setResults(data);
    } catch (_) {
      setResults([]);
    } finally {
      setSearching(false);
    }
  };

  const handleSetDob = async (user) => {
    const dob = dobEdits[user.id] ?? user.date_of_birth ?? "";
    if (!dob) {
      setDobMsg((m) => ({ ...m, [user.id]: "✗ Pick a date first" }));
      return;
    }
    setDobSaving((s) => ({ ...s, [user.id]: true }));
    setDobMsg((m) => ({ ...m, [user.id]: null }));
    try {
      const res = await practiceAdminSetDob(user.id, dob);
      // Reflect the saved value back into the row.
      setResults((rs) => rs.map((u) => (u.id === user.id ? { ...u, date_of_birth: res.date_of_birth } : u)));
      setDobMsg((m) => ({ ...m, [user.id]: "✓ Saved" }));
    } catch (e) {
      setDobMsg((m) => ({ ...m, [user.id]: `✗ ${e.message}` }));
    } finally {
      setDobSaving((s) => ({ ...s, [user.id]: false }));
    }
  };

  const handleReset = async (user) => {
    setResetting((r) => ({ ...r, [user.id]: true }));
    setResetMsg((m) => ({ ...m, [user.id]: null }));
    try {
      const res = await practiceAdminResetUser(user.id);
      setResetMsg((m) => ({
        ...m,
        [user.id]: `✓ ${res.sessions_cancelled} session(s) cancelled, ${res.lobby_entries_cleared} lobby entry cleared`,
      }));
    } catch (e) {
      setResetMsg((m) => ({ ...m, [user.id]: `✗ ${e.message}` }));
    } finally {
      setResetting((r) => ({ ...r, [user.id]: false }));
    }
  };

  return (
    <div className="space-y-6">
      {/* ── Service Status ── */}
      <div className={`rounded-xl p-4 space-y-3 ${limits.practice_calls_disabled ? "bg-rose-50 border-2 border-rose-300" : "surface-glass"}`}>
        <h3 className="text-sm font-semibold text-slate-700">Service Status</h3>
        {loadingLimits ? (
          <p className="text-xs text-slate-400">Loading…</p>
        ) : (
          <>
            <label className="flex items-start gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={limits.practice_calls_disabled ?? false}
                onChange={(e) => setLimits((l) => ({ ...l, practice_calls_disabled: e.target.checked }))}
                className="mt-0.5 h-4 w-4 rounded border-slate-300 text-rose-600 focus:ring-rose-400"
              />
              <span>
                <span className="text-sm text-slate-700 font-medium">Practice call service is down</span>
                <span className="block text-xs text-slate-400 mt-0.5">
                  When checked, students who open the practice call page will see a polite
                  service-unavailable notice instead of the lobby. No matching or calls will be possible.
                </span>
              </span>
            </label>
            {limits.practice_calls_disabled && (
              <p className="text-xs font-medium text-rose-700 bg-rose-100 rounded-lg px-3 py-2">
                ⚠️ Practice calls are currently disabled for all students.
              </p>
            )}

            <label className="flex items-start gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={limits.practice_calls_button_hidden ?? false}
                onChange={(e) => setLimits((l) => ({ ...l, practice_calls_button_hidden: e.target.checked }))}
                className="mt-0.5 h-4 w-4 rounded border-slate-300 text-amber-600 focus:ring-amber-400"
              />
              <span>
                <span className="text-sm text-slate-700 font-medium">Hide practice call button from students</span>
                <span className="block text-xs text-slate-400 mt-0.5">
                  When checked, the practice call button is hidden for all non-admin users.
                  Admins can still access the feature. The feature itself remains active.
                </span>
              </span>
            </label>
            {limits.practice_calls_button_hidden && (
              <p className="text-xs font-medium text-amber-700 bg-amber-50 rounded-lg px-3 py-2">
                ℹ️ Practice call button is currently hidden from students.
              </p>
            )}

            <label className="flex items-start gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={limits.practice_calls_require_18 ?? false}
                onChange={(e) => setLimits((l) => ({ ...l, practice_calls_require_18: e.target.checked }))}
                className="mt-0.5 h-4 w-4 rounded border-slate-300 text-emerald-600 focus:ring-emerald-400"
              />
              <span>
                <span className="text-sm text-slate-700 font-medium">Restrict practice calls to users 18 and over</span>
                <span className="block text-xs text-slate-400 mt-0.5">
                  When checked, only students aged 18+ may join practice calls. Students with no date
                  of birth on file are asked to add it before they can join. Admins are exempt.
                </span>
              </span>
            </label>
            {limits.practice_calls_require_18 && (
              <p className="text-xs font-medium text-emerald-700 bg-emerald-50 rounded-lg px-3 py-2">
                ℹ️ Practice calls are limited to students aged 18 and over.
              </p>
            )}
          </>
        )}
      </div>

      {/* ── Matching Settings ── */}
      <div className="surface-glass rounded-xl p-4 space-y-4">
        <h3 className="text-sm font-semibold text-slate-700">Matching Settings</h3>

        {loadingLimits ? (
          <p className="text-xs text-slate-400">Loading…</p>
        ) : (
          <>
            <label className="flex items-start gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={limits.require_topic_completion_for_calls ?? true}
                onChange={(e) => setLimits((l) => ({ ...l, require_topic_completion_for_calls: e.target.checked }))}
                className="mt-0.5 h-4 w-4 rounded border-slate-300 accent-[var(--gold)] focus:ring-[var(--gold)]"
              />
              <span>
                <span className="text-sm text-slate-700 font-medium">Require topic completion for calls</span>
                <span className="block text-xs text-slate-400 mt-0.5">
                  Students must complete topics 1, 2, 3… in order before joining the lobby.
                  Uncheck to allow any student to join regardless of progress (testing only).
                </span>
              </span>
            </label>

            <div className="flex items-center justify-between gap-4">
              <span>
                <p className="text-sm text-slate-700 font-medium">Min. overlap to match</p>
                <p className="text-xs text-slate-400 mt-0.5">
                  Students won't be matched unless their availability overlaps by at least this many minutes.
                </p>
              </span>
              <div className="flex items-center gap-1 shrink-0">
                <input
                  type="number" min="5" max="60" inputMode="numeric"
                  value={limits.min_practice_overlap_minutes ?? 15}
                  onChange={(e) => setLimits((l) => ({ ...l, min_practice_overlap_minutes: parseInt(e.target.value) || 15 }))}
                  className="w-16 px-2 py-1.5 rounded-lg border border-slate-200 text-sm text-center focus:outline-none focus:ring-2 focus:ring-[var(--gold)]"
                />
                <span className="text-xs text-slate-500">min</span>
              </div>
            </div>

            <div>
              <p className="text-sm text-slate-700 font-medium">Available call durations</p>
              <p className="text-xs text-slate-400 mt-0.5 mb-2">
                Which slot lengths students may choose when starting a lobby. At least one must stay selected.
              </p>
              <div className="flex flex-wrap gap-2">
                {[15, 30, 45, 60].map((d) => {
                  const active = (limits.practice_call_durations ?? []).includes(d);
                  return (
                    <button
                      key={d}
                      type="button"
                      onClick={() => toggleDuration(d)}
                      className={`px-4 py-2 rounded-lg text-sm font-medium border transition-colors ${
                        active
                          ? "bg-dt-gold-bright text-dt-ink border-[var(--gold)]"
                          : "bg-white text-slate-600 border-slate-200 hover:border-[var(--gold)]"
                      }`}
                    >
                      {d} min
                    </button>
                  );
                })}
              </div>
            </div>

            <div className="flex items-center justify-between gap-4">
              <span>
                <p className="text-sm text-slate-700 font-medium">Max calls per week</p>
                <p className="text-xs text-slate-400 mt-0.5">
                  How many practice calls a non-admin student may book per rolling 7-day window.
                  Set to 0 for unlimited. Admins are exempt.
                </p>
              </span>
              <div className="flex items-center gap-1 shrink-0">
                <input
                  type="number" min="0" max="50" inputMode="numeric"
                  value={limits.max_weekly_calls ?? 2}
                  onChange={(e) => {
                    const v = parseInt(e.target.value);
                    setLimits((l) => ({ ...l, max_weekly_calls: Number.isNaN(v) ? 0 : Math.max(0, v) }));
                  }}
                  className="w-16 px-2 py-1.5 rounded-lg border border-slate-200 text-sm text-center focus:outline-none focus:ring-2 focus:ring-[var(--gold)]"
                />
                <span className="text-xs text-slate-500">/wk</span>
              </div>
            </div>

            <div className="flex items-center justify-between gap-4">
              <span>
                <p className="text-sm text-slate-700 font-medium">Max participants per call</p>
                <p className="text-xs text-slate-400 mt-0.5">
                  The largest call size a host may choose when creating a lobby.
                  2 = classic one-to-one calls only.
                </p>
              </span>
              <div className="flex items-center gap-1 shrink-0">
                <input
                  type="number" min="2" max="10" inputMode="numeric"
                  value={limits.max_call_participants ?? 2}
                  onChange={(e) => {
                    const v = parseInt(e.target.value);
                    setLimits((l) => ({ ...l, max_call_participants: Number.isNaN(v) ? 2 : Math.min(10, Math.max(2, v)) }));
                  }}
                  className="w-16 px-2 py-1.5 rounded-lg border border-slate-200 text-sm text-center focus:outline-none focus:ring-2 focus:ring-[var(--gold)]"
                />
                <span className="text-xs text-slate-500">people</span>
              </div>
            </div>

            <div className="flex items-center gap-3 pt-1">
              <button
                onClick={handleSaveLimits}
                disabled={savingLimits}
                className="px-4 py-1.5 rounded-lg bg-dt-gold-bright text-dt-ink text-sm font-medium hover:brightness-105 disabled:opacity-50"
              >
                {savingLimits ? "Saving…" : "Save"}
              </button>
              {limitsMsg && (
                <span className={`text-xs font-medium ${limitsMsg === "Saved!" ? "text-emerald-600" : "text-rose-600"}`}>
                  {limitsMsg}
                </span>
              )}
            </div>
          </>
        )}
      </div>

      {/* ── Reset stuck user ── */}
      <div className="surface-glass rounded-xl p-4 space-y-3">
        <div>
          <h3 className="text-sm font-semibold text-slate-700 mb-1">Manage user</h3>
          <p className="text-xs text-slate-400">
            Reset a stuck user (cancels active/pending sessions and clears lobby state),
            or correct a user's date of birth — for example after a signup typo, or when the
            18+ restriction is on and a user can't join.
          </p>
        </div>
        <input
          type="text"
          placeholder="Search by username or email…"
          value={query}
          onChange={handleSearch}
          className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-[var(--gold)]"
        />

        {searching && <p className="text-xs text-slate-400">Searching…</p>}

        {results.length > 0 && (
          <ul className="space-y-2">
            {results.map((user) => (
              <li key={user.id} className="surface-glass p-3 rounded-lg space-y-2">
                <div className="flex items-center justify-between gap-3">
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-slate-800 truncate">{user.username}</p>
                    <p className="text-xs text-slate-400 truncate">{user.email}</p>
                    {resetMsg[user.id] && (
                      <p className={`text-xs mt-0.5 ${resetMsg[user.id].startsWith("✓") ? "text-emerald-600" : "text-rose-600"}`}>
                        {resetMsg[user.id]}
                      </p>
                    )}
                  </div>
                  <button
                    onClick={() => handleReset(user)}
                    disabled={resetting[user.id]}
                    className="shrink-0 px-3 py-1.5 rounded-lg bg-rose-600 hover:bg-rose-700 text-white text-xs font-medium disabled:opacity-50"
                  >
                    {resetting[user.id] ? "Resetting…" : "Reset"}
                  </button>
                </div>
                <div className="flex items-center gap-2 pt-2 border-t border-slate-50">
                  <span className="text-xs text-slate-500 shrink-0">Date of birth</span>
                  <input
                    type="date"
                    max={todayIso()}
                    value={dobEdits[user.id] ?? user.date_of_birth ?? ""}
                    onChange={(e) => setDobEdits((d) => ({ ...d, [user.id]: e.target.value }))}
                    className="px-2 py-1 rounded-lg border border-slate-200 text-xs focus:outline-none focus:ring-2 focus:ring-[var(--gold)]"
                  />
                  <button
                    onClick={() => handleSetDob(user)}
                    disabled={dobSaving[user.id]}
                    className="shrink-0 px-3 py-1 rounded-lg bg-dt-gold-bright hover:brightness-105 text-dt-ink text-xs font-medium disabled:opacity-50"
                  >
                    {dobSaving[user.id] ? "Saving…" : "Set"}
                  </button>
                  {dobMsg[user.id] && (
                    <span className={`text-xs ${dobMsg[user.id].startsWith("✓") ? "text-emerald-600" : "text-rose-600"}`}>
                      {dobMsg[user.id]}
                    </span>
                  )}
                </div>
              </li>
            ))}
          </ul>
        )}

        {!searching && query.trim() && results.length === 0 && (
          <p className="text-xs text-slate-400">No users found.</p>
        )}
      </div>

      {/* ── Misgendering Reports ── */}
      <AdminMisgenderingSection />
    </div>
  );
}

function AdminMisgenderingSection() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [setting, setSetting] = useState({});
  const [setMsg, setSetMsg] = useState({});

  const load = () => {
    setLoading(true);
    practiceAdminMisgenderingReports()
      .then(setData)
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  };

  useEffect(load, []);

  const handleSetGender = async (userId, gender) => {
    setSetting((s) => ({ ...s, [userId]: gender }));
    setSetMsg((m) => ({ ...m, [userId]: null }));
    try {
      await practiceAdminSetGender(userId, gender);
      setSetMsg((m) => ({ ...m, [userId]: "✓ Updated" }));
      load();
    } catch (e) {
      setSetMsg((m) => ({ ...m, [userId]: `✗ ${e.message}` }));
    } finally {
      setSetting((s) => ({ ...s, [userId]: null }));
    }
  };

  return (
    <div className="surface-glass rounded-xl p-4 space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-sm font-semibold text-slate-700">Misgendering Reports</h3>
          <p className="text-xs text-slate-400 mt-0.5">
            Users flagged by multiple students as misgendered. Threshold:{" "}
            {data ? <span className="font-medium text-slate-600">{data.threshold} report(s)</span> : "…"}
          </p>
        </div>
        <button
          onClick={load}
          className="text-xs text-dt-gold-bright hover:underline"
        >
          Refresh
        </button>
      </div>

      {loading && <p className="text-xs text-slate-400">Loading…</p>}
      {error && <p className="text-xs text-rose-600">{error}</p>}

      {data && (
        <>
          {/* Flagged users */}
          {data.flagged_users.length === 0 ? (
            <p className="text-xs text-slate-400 italic">No misgendering flags yet.</p>
          ) : (
            <ul className="space-y-2">
              {data.flagged_users.map((u) => (
                <li
                  key={u.user_id}
                  className={`surface-glass flex items-center gap-3 p-3 rounded-lg ${
                    u.above_threshold ? "border-2 border-rose-300" : ""
                  }`}
                >
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <p className="text-sm font-medium text-slate-800 truncate">{u.username}</p>
                      <span className={`text-xs px-1.5 py-0.5 rounded-full font-medium ${
                        u.gender === "male"
                          ? "bg-sky-100 text-sky-700"
                          : u.gender === "female"
                          ? "bg-fuchsia-100 text-fuchsia-700"
                          : "bg-slate-100 text-slate-500"
                      }`}>
                        {u.gender || "no gender"}
                      </span>
                      <span className={`text-xs px-1.5 py-0.5 rounded-full font-medium ${
                        u.above_threshold
                          ? "bg-rose-100 text-rose-700"
                          : "bg-amber-100 text-amber-700"
                      }`}>
                        {u.flag_count} flag{u.flag_count !== 1 ? "s" : ""}
                        {u.above_threshold ? " ⚠" : ""}
                      </span>
                    </div>
                    <p className="text-xs text-slate-400 truncate">{u.email}</p>
                    {u.gender_admin_changed_at && (
                      <p className="text-xs text-dt-gold-dim mt-0.5">
                        ✎ Gender corrected by admin on {new Date(u.gender_admin_changed_at).toLocaleString()}
                      </p>
                    )}
                    {setMsg[u.user_id] && (
                      <p className={`text-xs mt-1 ${setMsg[u.user_id].startsWith("✓") ? "text-emerald-600" : "text-rose-600"}`}>
                        {setMsg[u.user_id]}
                      </p>
                    )}
                  </div>
                  <div className="flex gap-1 shrink-0">
                    {["male", "female"].map((g) => (
                      <button
                        key={g}
                        onClick={() => handleSetGender(u.user_id, g)}
                        disabled={!!setting[u.user_id] || u.gender === g}
                        className={`px-2.5 py-1 rounded-lg text-xs font-medium transition-colors disabled:opacity-40 ${
                          u.gender === g
                            ? g === "male"
                              ? "bg-sky-200 text-sky-800 cursor-default"
                              : "bg-fuchsia-200 text-fuchsia-800 cursor-default"
                            : g === "male"
                            ? "bg-sky-100 text-sky-700 hover:bg-sky-200"
                            : "bg-fuchsia-100 text-fuchsia-700 hover:bg-fuchsia-200"
                        }`}
                      >
                        {setting[u.user_id] === g ? "…" : g === "male" ? "♂ Male" : "♀ Female"}
                      </button>
                    ))}
                  </div>
                </li>
              ))}
            </ul>
          )}

          {/* Recent misgendering flags */}
          {data.recent_reports.length > 0 && (
            <div>
              <p className="text-xs font-medium text-slate-500 mb-2">Recent misgendering flags</p>
              <ul className="space-y-1.5">
                {data.recent_reports.map((r) => (
                  <li key={r.id} className="surface-glass text-xs text-slate-600 rounded-lg px-3 py-2">
                    <span className="font-medium text-slate-700">{r.reporter}</span>
                    {" flagged "}
                    <span className="font-medium text-rose-700">{r.reported_user}</span>
                    {r.general_issue && (
                      <span className="text-slate-400"> — "{r.general_issue.slice(0, 80)}{r.general_issue.length > 80 ? "…" : ""}"</span>
                    )}
                    <span className="text-slate-300 ml-2">{new Date(r.created_at).toLocaleDateString()}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* General issue reports (free-text, with or without misgendering flag) */}
          {data.general_reports && data.general_reports.length > 0 && (
            <div>
              <p className="text-xs font-medium text-slate-500 mb-2">General issue reports</p>
              <ul className="space-y-1.5">
                {data.general_reports.map((r) => (
                  <li key={r.id} className="surface-glass rounded-lg px-3 py-2 space-y-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-xs font-medium text-slate-700">{r.reporter}</span>
                      <span className="text-xs text-slate-400">about</span>
                      <span className="text-xs font-medium text-slate-700">{r.reported_user}</span>
                      {r.flagged_misgendering && (
                        <span className="text-xs px-1.5 py-0.5 rounded-full bg-rose-100 text-rose-700 font-medium">+ misgendering flag</span>
                      )}
                      <span className="text-xs text-slate-300 ml-auto">{new Date(r.created_at).toLocaleDateString()}</span>
                    </div>
                    <p className="text-xs text-slate-600 leading-relaxed">"{r.general_issue}"</p>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </>
      )}
    </div>
  );
}

// ── Quran Curriculum Import Panel ────────────────────────────────────────────

function AdminQuranImportPanel() {
  const [topicsFile, setTopicsFile] = useState(null);
  const [indexFile, setIndexFile] = useState(null);
  const [metadataFile, setMetadataFile] = useState(null);
  const [importing, setImporting] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  const handleImport = async () => {
    if (!topicsFile && !metadataFile) {
      setError("Please select at least quran_topics.json or quran_word_metadata.json.");
      return;
    }
    setError(null);
    setResult(null);
    setImporting(true);
    try {
      const data = await importQuranCurriculum({
        quran_topics: topicsFile || undefined,
        quran_topics_index: indexFile || undefined,
        quran_word_metadata: metadataFile || undefined,
      });
      setResult(data);
    } catch (err) {
      setError(err.message || "Import failed");
    } finally {
      setImporting(false);
    }
  };

  return (
    <div className="space-y-5">
      <div>
        <h3 className="text-sm font-semibold text-slate-800 mb-1">Quran Curriculum Import</h3>
        <p className="text-xs text-slate-500 leading-relaxed">
          Upload the curriculum JSON files to import built-in Quran topics and word metadata.
          Import is idempotent — existing records are updated, not duplicated.
        </p>
      </div>

      {/* File inputs */}
      <div className="space-y-3">
        <FileInput
          label="quran_topics.json"
          description="Creates or updates built-in curriculum topics"
          accept=".json"
          onChange={setTopicsFile}
          file={topicsFile}
        />
        <FileInput
          label="quran_topics_index.json"
          description="Optional — used for validation"
          accept=".json"
          onChange={setIndexFile}
          file={indexFile}
        />
        <FileInput
          label="quran_word_metadata.json"
          description="Imports per-word linguistic metadata"
          accept=".json"
          onChange={setMetadataFile}
          file={metadataFile}
        />
      </div>

      {error && (
        <div className="p-3 bg-red-50 border border-red-200 rounded-xl text-sm text-red-700">
          {error}
        </div>
      )}

      {result && (
        <div className="p-4 bg-emerald-50 border border-emerald-200 rounded-xl">
          <p className="text-sm font-semibold text-emerald-800 mb-2">✅ Import complete</p>
          <dl className="space-y-1 text-sm">
            <div className="flex justify-between">
              <dt className="text-emerald-700">Topics imported</dt>
              <dd className="font-medium text-emerald-900">{result.topics_imported}</dd>
            </div>
            <div className="flex justify-between">
              <dt className="text-emerald-700">Topics updated</dt>
              <dd className="font-medium text-emerald-900">{result.topics_updated}</dd>
            </div>
            <div className="flex justify-between">
              <dt className="text-emerald-700">Word metadata imported</dt>
              <dd className="font-medium text-emerald-900">{result.words_imported}</dd>
            </div>
            <div className="flex justify-between">
              <dt className="text-emerald-700">Word metadata updated</dt>
              <dd className="font-medium text-emerald-900">{result.words_updated}</dd>
            </div>
          </dl>
          {result.errors && result.errors.length > 0 && (
            <div className="mt-3">
              <p className="text-xs font-semibold text-amber-700 mb-1">Warnings ({result.errors.length})</p>
              <ul className="text-xs text-amber-800 space-y-0.5 max-h-32 overflow-y-auto">
                {result.errors.map((e, i) => <li key={i}>⚠ {e}</li>)}
              </ul>
            </div>
          )}
        </div>
      )}

      <button
        onClick={handleImport}
        disabled={importing || (!topicsFile && !metadataFile)}
        className="w-full py-2.5 rounded-xl bg-dt-gold-bright text-dt-ink text-sm font-medium hover:brightness-105 transition-colors disabled:opacity-50"
      >
        {importing ? "Importing…" : "Import Curriculum"}
      </button>
    </div>
  );
}

function FileInput({ label, description, accept, onChange, file }) {
  const ref = useRef(null);
  return (
    <div className="flex flex-col gap-1">
      <div className="flex items-center justify-between">
        <div>
          <span className="text-xs font-mono font-medium text-slate-700">{label}</span>
          <span className="ml-2 text-xs text-slate-400">{description}</span>
        </div>
        {file && (
          <button
            onClick={() => { onChange(null); if (ref.current) ref.current.value = ""; }}
            className="text-xs text-slate-400 hover:text-red-500 transition-colors"
          >
            ✕
          </button>
        )}
      </div>
      <label className={`flex items-center gap-2 px-3 py-2.5 rounded-xl border-2 border-dashed cursor-pointer transition-colors ${file ? "border-emerald-300 bg-emerald-50" : "border-slate-200 bg-slate-50 hover:border-slate-300"}`}>
        <svg className={`w-4 h-4 shrink-0 ${file ? "text-emerald-500" : "text-slate-400"}`} fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
        </svg>
        <span className={`text-xs truncate ${file ? "text-emerald-700 font-medium" : "text-slate-400"}`}>
          {file ? file.name : "Click to select file"}
        </span>
        <input
          ref={ref}
          type="file"
          accept={accept}
          className="hidden"
          onChange={(e) => onChange(e.target.files?.[0] || null)}
        />
      </label>
    </div>
  );
}
